import { z } from "zod";

/**
 * Lenient Zod fragments shared by the read-only `*.view.ts` schemas behind the
 * resource show pages. Everything is nullish + `.passthrough()` so a drifting
 * backend payload degrades gracefully (the show page still renders) instead of
 * throwing on parse. These mirror the backend `MediaDbDto` / `PhotoDbDto` /
 * `ShowGenreRefDbDto` / cast DTO shapes.
 */
export const zPhoto = z
  .object({
    url: z.string().nullish(),
    blurhash: z.string().nullish(),
  })
  .passthrough();

export const zTeaser = z
  .object({
    url: z.string().nullish(),
    previewUrl: z.string().nullish(),
    thumbnailUrl: z.string().nullish(),
    thumbnailBlurhash: z.string().nullish(),
  })
  .passthrough();

export const zMedia = z
  .object({
    photos: z.array(zPhoto).nullish(),
    teasers: z.array(zTeaser).nullish(),
  })
  .passthrough();

export const zGenreRef = z
  .object({ id: z.string().nullish(), name: z.string().nullish() })
  .passthrough();

export const zCastMember = z
  .object({
    personId: z.string().nullish(),
    name: z.string().nullish(),
    characterName: z.string().nullish(),
    role: z.string().nullish(),
    department: z.string().nullish(),
    /** Name-only play credits: the locale `name` is written in. */
    nameLocale: z.string().nullish(),
    /** Name-only play credits: a Latin-alphabet form of a non-Latin `name`. */
    latinName: z.string().nullish(),
    photo: zPhoto.nullish(),
  })
  .passthrough();

/** DateTimeOffset values arrive as ISO strings from the admin getOne endpoint. */
export const zDate = z.union([z.string(), z.number()]).nullish();

/** AI enrichment reasoning ({@link AIReasoningDbDto}): rationale + 0–1 confidence. */
export const zReasoning = z
  .object({
    text: z.string().nullish(),
    confidence: z.number().nullish(),
  })
  .passthrough();

/** Per-language transliteration lists ({@link TransliterationsDbDto}). */
export const zTransliterations = z
  .object({
    en: z.array(z.string()).nullish(),
    el: z.array(z.string()).nullish(),
    ar: z.array(z.string()).nullish(),
    ro: z.array(z.string()).nullish(),
  })
  .passthrough();

/** A single FAQ entry ({@link FaqItemDbDto}). */
export const zFaq = z
  .object({
    question: z.string().nullish(),
    answer: z.string().nullish(),
  })
  .passthrough();

/**
 * Shared SEO/GEO contract emitted on every entity enrichment. Fields are
 * nullish so an entity that only carries part of the contract still parses.
 */
export const seoGeoFields = {
  normalizedTitle: z.string().nullish(),
  seoTitle: z.string().nullish(),
  shortDescription: z.string().nullish(),
  keywords: z.array(z.string()).nullish(),
  relatedSearches: z.array(z.string()).nullish(),
  faq: z.array(zFaq).nullish(),
};

/**
 * One locale's translated enrichment block ({@link EnrichmentTranslationDbDto}),
 * stored at `enrichment.translations.<locale>`. Every member nullish — one
 * shape serves eleven flows whose outputs differ (design D14).
 */
export const zTranslationBlock = z
  .object({
    seoTitle: z.string().nullish(),
    shortDescription: z.string().nullish(),
    about: z.string().nullish(),
    overview: z.string().nullish(),
    faq: z.array(zFaq).nullish(),
    keywords: z.array(z.string()).nullish(),
    relatedSearches: z.array(z.string()).nullish(),
  })
  .passthrough();

/** `enrichment.translations` — sparse, keyed by locale code. */
export const zTranslations = z.record(z.string(), zTranslationBlock).nullish();

/** The Tina creation/enrichment stamp nested under `metadata.tina`. */
export const zTinaStamp = z
  .object({
    id: z.string().nullish(),
    runId: z.string().nullish(),
    site: z.string().nullish(),
    url: z.string().nullish(),
    timestamp: z.union([z.string(), z.number()]).nullish(),
  })
  .passthrough();

/** Audit fields present on every resource DTO. */
export const systemFields = {
  createdBy: z.string().nullish(),
  updatedBy: z.string().nullish(),
  createdAt: zDate,
  updatedAt: zDate,
};
